import React, { useMemo } from 'react';
import * as Tooltip from '@radix-ui/react-tooltip';
import { useStore } from '../../stores';
import {
  buildVariableContext,
  previewVariable,
} from '../../lib/http/variableInterpolation';

// Pattern to match {{variable}} with capture groups for before, variable name, and after
const VARIABLE_REGEX = /(\{\{)([^{}]+)(\}\})/g;

interface VariablePreviewInfo {
  value: string;
  scope: string;
  isSecret: boolean;
}

interface VariableQuickLookProps {
  /** The text containing {{variables}} to render with hover previews */
  text: string;
  /** Additional class names for the container */
  className?: string;
  /** Whether to mask secret values (default: true) */
  maskSecrets?: boolean;
}

/**
 * Renders text with {{variable}} patterns highlighted and showing
 * resolved values on hover
 */
export function VariableQuickLook({
  text,
  className,
  maskSecrets = true,
}: VariableQuickLookProps) {
  const { environments, activeEnvironmentId } = useStore();

  // Build variable context from current environment state
  const variableContext = useMemo(() => {
    const activeEnvironment = environments.find(
      (e) => e.id === activeEnvironmentId
    );
    const globalEnvironment = environments.find((e) => e.isGlobal);
    return buildVariableContext(activeEnvironment, globalEnvironment);
  }, [environments, activeEnvironmentId]);

  // Parse text into segments of plain text and variables
  const segments = useMemo(() => {
    if (!text) return [];

    const result: Array<
      | { type: 'text'; content: string }
      | { type: 'variable'; name: string; preview: VariablePreviewInfo }
    > = [];

    let lastIndex = 0;
    const regex = new RegExp(VARIABLE_REGEX.source, 'g');
    let match;

    while ((match = regex.exec(text)) !== null) {
      // Add text before the variable
      if (match.index > lastIndex) {
        result.push({
          type: 'text',
          content: text.slice(lastIndex, match.index),
        });
      }

      // Add the variable
      const varName = match[2].trim();
      const preview = previewVariable(varName, variableContext, maskSecrets);
      result.push({
        type: 'variable',
        name: varName,
        preview,
      });

      lastIndex = regex.lastIndex;
    }

    // Add remaining text
    if (lastIndex < text.length) {
      result.push({
        type: 'text',
        content: text.slice(lastIndex),
      });
    }

    return result;
  }, [text, variableContext, maskSecrets]);

  // If no segments, return null
  if (segments.length === 0) {
    return <span className={className}>{text}</span>;
  }

  return (
    <Tooltip.Provider delayDuration={200}>
      <span className={className}>
        {segments.map((segment, index) => {
          if (segment.type === 'text') {
            return <span key={index}>{segment.content}</span>;
          }

          const isUnresolved = segment.preview.scope === 'unresolved';

          return (
            <Tooltip.Root key={index}>
              <Tooltip.Trigger asChild>
                <span
                  className={`inline-flex items-center px-1 py-0.5 rounded text-xs font-mono cursor-help ${
                    isUnresolved
                      ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                      : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                  }`}
                >
                  {`{{${segment.name}}}`}
                </span>
              </Tooltip.Trigger>
              <Tooltip.Portal>
                <Tooltip.Content
                  className="z-50 rounded-md bg-popover px-3 py-2 text-sm text-popover-foreground shadow-md border border-border animate-in fade-in-0 zoom-in-95"
                  sideOffset={5}
                >
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground text-xs">
                        Value:
                      </span>
                      <span className="font-mono text-xs">
                        {segment.preview.value || '(empty)'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground text-xs">
                        Scope:
                      </span>
                      <span
                        className={`text-xs px-1.5 py-0.5 rounded ${
                          isUnresolved
                            ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                            : segment.preview.scope === 'environment'
                              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                              : segment.preview.scope === 'global'
                                ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                                : 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                        }`}
                      >
                        {segment.preview.scope}
                      </span>
                    </div>
                    {segment.preview.isSecret && (
                      <div className="text-xs text-muted-foreground italic">
                        Secret variable
                      </div>
                    )}
                  </div>
                  <Tooltip.Arrow className="fill-popover" />
                </Tooltip.Content>
              </Tooltip.Portal>
            </Tooltip.Root>
          );
        })}
      </span>
    </Tooltip.Provider>
  );
}

interface VariableHighlightInputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  /** Whether to show the variable preview overlay */
  showPreview?: boolean;
}

/**
 * An input component that shows variable previews on hover.
 * This renders a hidden preview layer over the input that shows
 * variable tooltips.
 */
export function VariableHighlightInput({
  showPreview = true,
  value,
  className,
  ...props
}: VariableHighlightInputProps) {
  const stringValue = String(value || '');
  const hasVariables = /\{\{[^{}]+\}\}/.test(stringValue);

  if (!showPreview || !hasVariables) {
    return <input value={value} className={className} {...props} />;
  }

  return (
    <div className="relative">
      <input value={value} className={className} {...props} />
      {/* Overlay for variable preview - positioned over the input */}
      <div className="absolute inset-0 pointer-events-none flex items-center px-3 font-mono text-sm overflow-hidden">
        <div className="pointer-events-auto">
          <VariableQuickLook text={stringValue} className="invisible" />
        </div>
      </div>
    </div>
  );
}

/**
 * Hook to get variable preview info for a single variable name
 */
export function useVariablePreview(variableName: string, maskSecrets = true) {
  const { environments, activeEnvironmentId } = useStore();

  return useMemo(() => {
    const activeEnvironment = environments.find(
      (e) => e.id === activeEnvironmentId
    );
    const globalEnvironment = environments.find((e) => e.isGlobal);
    const context = buildVariableContext(activeEnvironment, globalEnvironment);
    return previewVariable(variableName, context, maskSecrets);
  }, [variableName, environments, activeEnvironmentId, maskSecrets]);
}
